import React, { useState } from 'react';
import type { Person } from './types';
import { X, Users, Heart, Baby, UserPlus, Search } from 'lucide-react';

interface RelationshipManagerProps {
  person: Person;
  people: Record<string, Person>;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedPerson: Person) => void;
}

const RelationshipManager: React.FC<RelationshipManagerProps> = ({
  person,
  people,
  isOpen,
  onClose,
  onUpdate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<'parents' | 'spouses' | 'children'>('parents');

  const availablePeople = Object.values(people)
    .filter(p => p.id !== person.id)
    .filter(p => 
      searchTerm === '' || 
      p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const getRelatedPeople = (relationshipType: 'parents' | 'spouses' | 'children') => {
    const relationshipIds = person[relationshipType] || [];
    return relationshipIds
      .map(id => people[id])
      .filter(Boolean);
  };

  const addRelationship = (targetPersonId: string, relationshipType: 'parents' | 'spouses' | 'children') => {
    const updatedPerson = { ...person };
    const currentRelationships = updatedPerson[relationshipType] || [];
    
    if (!currentRelationships.includes(targetPersonId)) {
      updatedPerson[relationshipType] = [...currentRelationships, targetPersonId];
      
      // Update the reciprocal relationship
      const targetPerson = people[targetPersonId];
      if (targetPerson) {
        const updatedTargetPerson = { ...targetPerson };
        
        if (relationshipType === 'parents') {
          // If adding a parent, add this person as their child
          const targetChildren = updatedTargetPerson.children || [];
          if (!targetChildren.includes(person.id)) {
            updatedTargetPerson.children = [...targetChildren, person.id];
          }
        } else if (relationshipType === 'children') {
          // If adding a child, add this person as their parent
          const targetParents = updatedTargetPerson.parents || [];
          if (!targetParents.includes(person.id)) {
            updatedTargetPerson.parents = [...targetParents, person.id];
          }
        } else if (relationshipType === 'spouses') {
          // If adding a spouse, add this person as their spouse
          const targetSpouses = updatedTargetPerson.spouses || [];
          if (!targetSpouses.includes(person.id)) {
            updatedTargetPerson.spouses = [...targetSpouses, person.id];
          }
        }
        
        // In a real app, you'd update the target person in the database too
        // For now, we'll just update the current person
      }
      
      onUpdate(updatedPerson);
    }
  };

  const removeRelationship = (targetPersonId: string, relationshipType: 'parents' | 'spouses' | 'children') => {
    const updatedPerson = { ...person };
    const currentRelationships = updatedPerson[relationshipType] || [];
    updatedPerson[relationshipType] = currentRelationships.filter(id => id !== targetPersonId);
    
    onUpdate(updatedPerson);
  };

  const isRelated = (targetPersonId: string, relationshipType: 'parents' | 'spouses' | 'children') => {
    const relationships = person[relationshipType] || [];
    return relationships.includes(targetPersonId);
  };

  const getGenderAppropriateLabel = (relationshipType: 'parents' | 'spouses' | 'children', targetPerson: Person) => {
    if (relationshipType === 'parents') {
      return targetPerson.gender === 'male' ? 'Father' : 
             targetPerson.gender === 'female' ? 'Mother' : 'Parent';
    }
    if (relationshipType === 'spouses') {
      return targetPerson.gender === 'male' ? 'Husband' : 
             targetPerson.gender === 'female' ? 'Wife' : 'Spouse';
    }
    if (relationshipType === 'children') {
      return targetPerson.gender === 'male' ? 'Son' : 
             targetPerson.gender === 'female' ? 'Daughter' : 'Child';
    }
    return '';
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'parents', label: 'Parents', icon: Users, count: getRelatedPeople('parents').length },
    { id: 'spouses', label: 'Spouses', icon: Heart, count: getRelatedPeople('spouses').length },
    { id: 'children', label: 'Children', icon: Baby, count: getRelatedPeople('children').length },
  ] as const;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">
            Manage Relationships for {person.name}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Tabs */}
          <div className="flex border-b mb-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`flex items-center px-4 py-2 font-medium border-b-2 transition-colors ${
                    selectedTab === tab.id
                      ? 'border-ocean-teal text-ocean-teal'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="ml-2 bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Current Relationships */}
            <div>
              <h3 className="text-lg font-semibold mb-4">
                Current {tabs.find(t => t.id === selectedTab)?.label}
              </h3>
              
              {getRelatedPeople(selectedTab).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No {selectedTab} added yet
                </div>
              ) : (
                <div className="space-y-3">
                  {getRelatedPeople(selectedTab).map((relatedPerson) => (
                    <div key={relatedPerson.id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-700">{relatedPerson.name}</h4>
                        <p className="text-sm text-gray-500">
                          {getGenderAppropriateLabel(selectedTab, relatedPerson)}
                          {relatedPerson.birthDate && ` • Born ${relatedPerson.birthDate.slice(0, 4)}`}
                        </p>
                        {relatedPerson.iwi && relatedPerson.iwi.length > 0 && (
                          <p className="text-xs text-purple-600 mt-1">{relatedPerson.iwi[0]}</p>
                        )}
                      </div>
                      <button
                        onClick={() => removeRelationship(relatedPerson.id, selectedTab)}
                        className="p-2 text-ocean-teal hover:bg-ocean-blue hover:bg-opacity-10 rounded"
                        title="Remove relationship"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Relationships */}
            <div>
              <h3 className="text-lg font-semibold mb-4">
                Add {tabs.find(t => t.id === selectedTab)?.label.slice(0, -1)}
              </h3>
              
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search people..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-teal"
                />
              </div>

              {/* Available People */}
              <div className="max-h-64 overflow-y-auto space-y-2">
                {availablePeople
                  .filter(p => !isRelated(p.id, selectedTab))
                  .map((availablePerson) => (
                    <div key={availablePerson.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-700">{availablePerson.name}</h4>
                        <p className="text-sm text-gray-500">
                          {availablePerson.gender.charAt(0).toUpperCase() + availablePerson.gender.slice(1)}
                          {availablePerson.birthDate && ` • Born ${availablePerson.birthDate.slice(0, 4)}`}
                        </p>
                        {availablePerson.iwi && availablePerson.iwi.length > 0 && (
                          <p className="text-xs text-purple-600 mt-1">{availablePerson.iwi[0]}</p>
                        )}
                      </div>
                      <button
                        onClick={() => addRelationship(availablePerson.id, selectedTab)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded"
                        title={`Add as ${selectedTab.slice(0, -1)}`}
                      >
                        <UserPlus className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                }
                
                {availablePeople.filter(p => !isRelated(p.id, selectedTab)).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {searchTerm ? 'No matching people found' : 'All available people are already related'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end mt-6 pt-4 border-t">
            <button
              onClick={onClose}
              className="btn-primary"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelationshipManager;
